import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  IconButton,
  Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { captureUnexpectedException } from '../../../utils/errorHandler';
import {
  gray100,
  gray80,
  gray60,
  orange50,
  primaryButtonBackgroundColor,
  white,
} from '../../../constants/colors';
import { titleMb } from '../../../constants/location';
import { groupLimit, registerStatus } from '../../../constants/enum';
import { GROUP_CREATE_TITLE, GROUP_EDIT_TITLE } from '../../../constants/labels';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import { useCompanyId } from '../../../hooks/useCompanyId';
import { usePageLayout } from '../../../hooks/usePageLayout';
import { isNotCompanyMember } from '../../../utils/permission';
import SideBar from '../../uiParts/menu/SideBar';
import MetaTitle from '../../utils/MetaTitle';
import CustomTextfield from '../../uiParts/form/CustomTextfield';
import CustomLoading from '../../uiParts/form/CustomLoading';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import { createGroup } from '../../../api/firebase/firestore/group/create';
import { updateGroup } from '../../../api/firebase/firestore/group/update';
import { getGroup } from '../../../api/firebase/firestore/group/read';
import { setGroupMembers } from '../../../api/firebase/firestore/group/member';
import { getUsersByCompanyId } from '../../../api/firebase/firestore/user/read';
import type { UserType } from '../../../types/user';

const styles = {
  cardSize: {
    boxShadow: 0,
    marginLeft: '16px',
    marginRight: '16px',
    width: 480,
    '@media screen and (max-width:480px)': { width: 343 },
  },
  cardContentPadding: {
    padding: '40px',
    '&:last-child': { paddingBottom: '40px' },
    '@media screen and (max-width:480px)': { padding: '32px 16px' },
  },
  submitButton: {
    height: '42px',
  },
  label: { fontSize: '14px', color: gray80, marginBottom: '8px', textAlign: 'left' },
  memberList: {
    maxHeight: '200px',
    overflowY: 'auto',
    marginBottom: '32px',
  },
  memberRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    // チェックボックスをラベル「メンバー」の左端に揃えるため左パディングは持たせない（#2804）
    padding: '4px 0',
    cursor: 'pointer',
  },
  memberName: { fontSize: '14px', color: gray100 },
  memberSub: { fontSize: '12px', color: gray60 },
};

/**
 * グループ作成・編集画面（#2804）
 * 担当者登録/編集（StaffRegister/StaffEdit）と同様、中央カード型の独立画面。
 * - /group/register        → 新規作成
 * - /group/:groupId/edit    → 編集
 * 説明欄の下に所属企業メンバーのチェックボックス一覧を表示し、所属の追加・除外を行う。
 * 企業管理者・運営のみアクセス可能。
 */
const GroupRegister = (): React.ReactElement => {
  const { user, isLoading } = useContext(AuthContext);
  const { companyId: effectiveCompanyId } = useCompanyId();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const location = useLocation();
  // 担当者編集（/staff/edit）と同様、編集対象IDはURLではなく state で受け取る
  const locationState = location.state as { groupId?: string; from?: string } | null;
  const groupId = locationState?.groupId;
  const isEdit = location.pathname === '/group/edit';
  // 遷移元（グループ管理 or 担当者一覧）。保存・キャンセル時にここへ戻る
  const from = locationState?.from ?? '/group';

  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [companyUsers, setCompanyUsers] = useState<UserType[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [touched, setTouched] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(true);
  const [errorText, setErrorText] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

  const companyId = effectiveCompanyId || user?.company_id;

  useEffect(() => {
    if (isLoading) return;
    if (user === null) {
      return;
    }
    if (!isNotCompanyMember(user.role)) {
      navigate('/pattern');
    }
  }, [user, isLoading, navigate]);

  // 企業メンバー一覧と、編集時は対象グループを取得
  useEffect(() => {
    if (!companyId || !user) return;
    // 編集なのに対象IDが無い（state無しの直接アクセス）場合は一覧へ戻す
    if (isEdit && !groupId) {
      navigate('/group');
      return;
    }
    const fetchData = async (): Promise<void> => {
      setFetching(true);
      try {
        const users = (await getUsersByCompanyId(companyId)) as UserType[];
        // 招待中（メール登録のみ）のユーザーはグループに追加できないため、
        // 登録完了（有効）ステータスのメンバーのみを選択肢として表示する（#2804）
        // ※削除済みは getUsersByCompanyId のクエリ段階で既に除外されている
        const activeUsers = users.filter(u => u.register_status === registerStatus.finished);
        setCompanyUsers(activeUsers);
        const activeUserIds = new Set(activeUsers.map(u => u.uid));

        if (isEdit && groupId) {
          const group = await getGroup(companyId, groupId);
          if (!group) {
            navigate('/group');
            return;
          }
          setName(group.name);
          setDescription(group.description ?? '');
          // 一覧に表示される有効メンバーのみを選択状態にし、表示と件数・保存内容を一致させる
          // （過去データで招待中ユーザーが member_ids に含まれていても件数がズレない）
          setSelectedMemberIds(new Set(group.member_ids.filter(id => activeUserIds.has(id))));
        } else {
          // 新規作成時は作成者を初期メンバーとして選択
          setSelectedMemberIds(new Set([user.uid]));
        }
      } catch (error) {
        Sentry.captureException(error);
        setErrorText('情報の取得に失敗しました');
        setSnackbarOpen(true);
      } finally {
        setFetching(false);
      }
    };
    void fetchData();
  }, [companyId, user, isEdit, groupId, navigate]);

  const trimmedName = name.trim();
  const isNameValid = trimmedName.length > 0 && trimmedName.length <= groupLimit.groupNameMaxLength;
  const isDescriptionValid = description.length <= groupLimit.groupDescriptionMaxLength;
  const canSubmit = isNameValid && isDescriptionValid && !submitting;

  const nameError = touched && !isNameValid;
  const nameHelper = nameError
    ? trimmedName.length === 0
      ? 'グループ名を入力してください'
      : `グループ名は${groupLimit.groupNameMaxLength}文字以内で入力してください`
    : undefined;
  const descriptionHelper = !isDescriptionValid
    ? `説明は${groupLimit.groupDescriptionMaxLength}文字以内で入力してください`
    : undefined;

  const toggleMember = (uid: string): void => {
    setSelectedMemberIds(prev => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  };

  const handleSubmit = async (): Promise<void> => {
    setTouched(true);
    if (!canSubmit || !companyId || !user) return;
    setSubmitting(true);
    try {
      const values = { name: trimmedName, description: description.trim() || null };
      const memberIds = Array.from(selectedMemberIds);
      if (isEdit && groupId) {
        await updateGroup(companyId, groupId, values, user.uid);
        await setGroupMembers(companyId, groupId, memberIds, user.uid);
      } else {
        await createGroup(companyId, values, memberIds, user.uid);
      }
      navigate(from);
    } catch (error) {
      // 上限超過などの想定内エラー(ValidationError)はSentry通知をスキップ #2883
      captureUnexpectedException(error);
      setErrorText(
        error instanceof Error
          ? error.message
          : isEdit
            ? 'グループの更新に失敗しました'
            : 'グループの作成に失敗しました',
      );
      setSnackbarOpen(true);
      setSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate(from);
  };

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>
      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          { justifyContent: 'center', alignItems: 'center', minHeight: '100vh' },
        ]}
      >
        {fetching ? (
          <CustomLoading loading={true} />
        ) : (
          <Card sx={styles.cardSize}>
            <CardContent sx={styles.cardContentPadding}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginBottom: '24px',
                }}
              >
                <IconButton
                  onClick={handleCancel}
                  size="small"
                  disabled={submitting}
                  aria-label="戻る"
                  sx={{ marginLeft: '-8px' }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography
                  sx={[titleMb, { color: gray100, textAlign: 'left', marginBottom: 0 }]}
                  component="div"
                >
                  {isEdit ? GROUP_EDIT_TITLE : GROUP_CREATE_TITLE}
                </Typography>
              </Box>

              <CustomTextfield
                labelText="グループ名"
                requiredText="必須"
                required
                placeholder="例: 経理部"
                value={name}
                onChange={(e): void => setName(e.target.value)}
                error={nameError}
                helperText={nameHelper}
                formMb="24px"
                width="100%"
                inputWidth="100%"
              />

              <CustomTextfield
                labelText="説明"
                placeholder="グループの説明（任意）"
                value={description}
                onChange={(e): void => setDescription(e.target.value)}
                multiline
                minRows={3}
                error={!isDescriptionValid}
                helperText={descriptionHelper}
                formMb="24px"
                width="100%"
                inputWidth="100%"
                height="auto"
              />

              <Typography sx={styles.label}>メンバー（{selectedMemberIds.size}名）</Typography>
              <Box sx={styles.memberList}>
                {companyUsers.length === 0 ? (
                  <Typography sx={{ ...styles.memberSub, padding: '16px', textAlign: 'center' }}>
                    メンバーがいません
                  </Typography>
                ) : (
                  companyUsers.map(u => (
                    <Box
                      key={u.uid}
                      sx={styles.memberRow}
                      onClick={(): void => toggleMember(u.uid)}
                    >
                      <Checkbox
                        checked={selectedMemberIds.has(u.uid)}
                        // 内部パディング分を負マージンで打ち消し、チェック枠の左端をラベル「メンバー」左端に合わせる
                        sx={{
                          '&.Mui-checked': { color: orange50 },
                          padding: '4px',
                          marginLeft: '-4px',
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                        <Typography sx={styles.memberName}>{u.name || '(名前未設定)'}</Typography>
                        {u.department && (
                          <Typography sx={styles.memberSub}>{u.department}</Typography>
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>

              <Button
                variant="contained"
                fullWidth
                sx={[primaryButtonBackgroundColor, styles.submitButton]}
                disabled={touched && !canSubmit}
                onClick={(): void => void handleSubmit()}
              >
                {submitting ? (
                  <CircularProgress size={20} sx={{ color: white }} />
                ) : (
                  <Typography component="div" color="white" sx={{ fontSize: 16, fontWeight: 700 }}>
                    {isEdit ? '保存する' : '作成する'}
                  </Typography>
                )}
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
      />
    </Box>
  );
};

export default GroupRegister;
